import { take, call, put, fork, race, takeEvery, takeLatest } from 'redux-saga/effects';
import URL from 'url-parse';
import qs from 'querystringify';
import { toast } from 'react-toastify';

import { success, info, warning, error } from 'react-notification-system-redux';
import {
} from './actions';
import {
  APP_STARTUP,
  APP_CHANGE_FORM, APP_OAUTH_LOGIN_REQUEST, APP_CHECK_TOKEN, APP_LOGIN_REQUEST, APP_LOGOUT,
  APP_REGISTER_REQUEST,
  APP_REQUEST_ERROR,
  APP_SENDING_REQUEST,
  APP_SET_AUTH, APP_NOTIFICATION, USER_LOGGEDIN,
  PROJECTS_LOADED,CATEGORIES_LOADED

} from './constants';
import Api from './api';
import { push } from 'react-router-redux';
import routes from './routes';
import { loginRequest } from './actions';

/**
 * startup bootstrapping with data
 */
function* loadStartup() {
  try {
    yield loadCategories();
    yield loadProjects();
  } catch (er) {
    yield put({ type: APP_REQUEST_ERROR, payload: er });
  }
}

function* loadCategories() {
  try {
    const res = yield call(Api.Categories.getAll);
    yield put({ type: CATEGORIES_LOADED, payload: res });
  } catch (er) {
    yield put({ type: APP_REQUEST_ERROR, payload: er });
  }
}
function* loadProjects() {
  try {
    const res = yield call(Api.PublishedGames.getAll);
    yield put({ type: PROJECTS_LOADED, payload: res });
  } catch (er) {
    yield put({ type: APP_REQUEST_ERROR, payload: er });
  }
}

/**
 * Login & Register
 */
export function* authorize({ username, password, isRegistering }) {
  yield put({ type: APP_SENDING_REQUEST, sending: true });

  try {
    let response;

    if (isRegistering) {
      response = yield call(Api.User.register, username);
    } else {
      response = yield call(Api.User.login, username, password);
    }
    return response;
  } catch (error) {
    yield put({ type: APP_REQUEST_ERROR, error: error.message });
  } finally {
    yield put({ type: APP_SENDING_REQUEST, sending: false });
  }
}

/**
 * Effect to handle logging out
 */
export function* logout() {
  yield put({ type: APP_SENDING_REQUEST, sending: true });

  try {
    const response = yield call(Api.User.logout);
    yield put({ type: APP_SENDING_REQUEST, sending: false });

    return response;
  } catch (error) {
    yield put({ type: APP_REQUEST_ERROR, error: error.message });
  }
}

/**
 * Log in saga
 */
export function* loginFlow() {
  while (true) {
    const request = yield take(APP_LOGIN_REQUEST);
    const { username, password } = request.data;

    const winner = yield race({
      auth: call(authorize, { username, password, isRegistering: false }),
      logout: take(APP_LOGOUT),
    });

    if (winner.auth) {
      yield put({ type: APP_SET_AUTH, newAuthState: true });
      yield put({ type: APP_CHANGE_FORM, newFormState: { username: '', password: '' } });
    }
  }
}

/**
 * Log out saga
 * This is basically the same as the `if (winner.logout)` of above, just written
 * as a saga that is always listening to `LOGOUT` actions
 */
export function* logoutFlow() {
  while (true) {
    yield take(APP_LOGOUT);
    yield put({ type: APP_SET_AUTH, newAuthState: false });
    yield call(Api.removeToken);

    yield call(logout);
  }
}

/**
 * Register saga
 * Very similar to log in saga!
 */
export function* registerFlow() {
  while (true) {
    const request = yield take(APP_REGISTER_REQUEST);
    const {
      email, first_name, last_name, password,
    } = request.payload;

    try {
      const wasSuccessful = yield call(Api.User.register, email, first_name, last_name, password);

      if (wasSuccessful) {
        yield put(loginRequest({ username: email, password }));
        yield put(push(routes.EDITOR_START));
      }
    } catch (error) {
      yield put({ type: APP_REQUEST_ERROR, error: error.message });
    }
  }
}

export function* tokenFlow() {
  while (true) {
    const request = yield take(APP_CHECK_TOKEN);
    //only to use request (eslint)
    let response = request;
    response = false;
    if (Api.checkTokenAvailable() !== null) {
      response = yield call(Api.User.isLoggedIn);
    }

    const URI = new URL(window.location.href, true);
    if (URI.query && ['facebook', 'twitter', 'google'].includes(URI.query.service)) {
      response = yield call(Api.User.loginWithOauth, qs.stringify(URI.query));

      yield put({ type: USER_LOGGEDIN, payload: response.data });
      yield put(push(routes.EDITOR_START));
    }

    if (response === false) {
      yield put({ type: APP_SET_AUTH, newAuthState: false });
      yield put({ type: APP_SENDING_REQUEST, sending: false });
      break;
    } else {
      yield put({ type: APP_SET_AUTH, newAuthState: true });
      yield put({ type: APP_CHANGE_FORM, newFormState: { username: '', password: '' } });
      yield put({ type: APP_SENDING_REQUEST, sending: false });
      break;
    }
  }
}

export function* oAuthLoginFlow() {
  while (true) {
    const request = yield take(APP_OAUTH_LOGIN_REQUEST);
    const response = yield call(Api.User.oAuthLogin, request.payload.service);

    window.location = response.data.response.url;

    break;
  }
}

export function* notify(action) {
  const notify = {
    // uid: 'once-please', // you can specify your own uid if required
    title: action.payload.title,
    message: action.payload.message,
    position: 'br',
    autoDismiss: 5,
  };
  if (action.payload.type === 'success') { yield put(success(notify)); }
  if (action.payload.type === 'warning') { yield put(warning(notify)); }
  if (action.payload.type === 'info') { yield put(info(notify)); }
  if (action.payload.type === 'error') { yield put(error(notify)); }

  yield toast(action.payload.message);
}

export function* notifyRequestError() {
  yield put({ type: APP_NOTIFICATION, payload: { type: 'error', title: 'Upps...', message: 'Während des Datenaustausch ist ein Fehler aufgetreten. Versuche es noch einmal.' } });
}

// The root saga is what we actually send to Redux's middleware. In here we fork
// each saga so that they are all "active" and listening.
// Sagas are fired once at the start of an app and can be thought of as processes running
// in the background, watching actions dispatched to the store.
export default function* root() {
  yield takeLatest(APP_STARTUP, loadStartup);
  yield fork(tokenFlow);
  yield fork(loginFlow);
  yield fork(oAuthLoginFlow);
  yield fork(logoutFlow);
  yield fork(registerFlow);
  yield takeLatest(APP_NOTIFICATION, notify);
  yield takeEvery(APP_REQUEST_ERROR, notifyRequestError);
}



// WEBPACK FOOTER //
// ./src/saga.js